import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Checkbox, Typography } from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";

import LoadingSpinner from "../../components/LoadingSpinner";
import BottomActionBar from "../../components/BottomActionBar";
import useApiCall from "../../hooks/useApiCall";
import useToast from "../../hooks/useToast";
import { TOAST } from "../../constants/ToastMessages";
import { usePageMeta } from "../../contexts/PageMetaContext";
import { INDIGO, SLATE } from "../../constants/Colors";

const COL_HEADER_SX = {
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: SLATE[500],
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

/** Displays "development_37_us_careerlauncher.json" instead of the full S3 key. */
function displayName(fileName) {
  return fileName.replace(/^self_serve_data_transfer\//, "");
}

/** Formats an S3 last_modified timestamp as e.g. "Jul 21, 10:20" (UTC). */
function formatTimestamp(isoString) {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;

  const datePart = date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
  return `${datePart}, ${timePart}`;
}

export default function ImportClients() {
  const navigate = useNavigate();
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { success, error: showError } = useToast();
  const { setPageMeta } = usePageMeta();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    setPageMeta({ description: "Select previously exported client files to import" });
    return () => setPageMeta({});
  }, [setPageMeta]);

  useEffect(() => {
    makeRequest("/v1/export_files", { method: "GET", authTokens: tokens, ignore_error: true })
      .then((data) => setFiles(Array.isArray(data?.files) ? data.files : []))
      .catch(() => showError(TOAST.clients.loadFilesError))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAll = (event) => {
    setSelectedFiles(event.target.checked ? files.map((file) => file.key) : []);
  };

  const handleToggleFile = (fileName) => {
    setSelectedFiles((prev) =>
      prev.includes(fileName) ? prev.filter((f) => f !== fileName) : [...prev, fileName]
    );
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      for (const fileName of selectedFiles) {
        await makeRequest("/v1/import_from_file", {
          method: "POST",
          authTokens: tokens,
          body: { file_name: fileName },
          ignore_error: true,
        });
      }
      success(TOAST.clients.imported(selectedFiles.length));
      navigate("/clients");
    } catch {
      showError(TOAST.clients.importError);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading export files..." subMessage="Fetching files available for import" />;
  }

  const allSelected = files.length > 0 && selectedFiles.length === files.length;
  const someSelected = selectedFiles.length > 0 && !allSelected;

  return (
    <Box sx={{ width: "100%", pb: 10 }}>
      {/* Info banner */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          px: 2.5,
          py: 1.5,
          mb: 2,
          borderRadius: "10px",
          bgcolor: SLATE[50],
          border: `1px solid ${SLATE[200]}`,
        }}
      >
        <InfoIcon sx={{ fontSize: "1rem", color: SLATE[400], mt: "2px", flexShrink: 0 }} />
        <Box>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: SLATE[700], lineHeight: 1.4 }}>
            Import a previously exported client
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: SLATE[500], mt: "2px" }}>
            Each file was exported from a development environment via the "Export App" button on a
            client's Agents page. Importing creates or updates the client with everything in the file
            — agents, business units, websites, and settings.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "56px 1fr 160px",
            alignItems: "center",
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${SLATE[200]}`,
            bgcolor: SLATE[50],
          }}
        >
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
            disabled={files.length === 0}
          />
          <Typography sx={COL_HEADER_SX}>File name</Typography>
          <Typography sx={COL_HEADER_SX}>Created</Typography>
        </Box>

        {files.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10, width: "100%" }}>
            <Typography sx={{ fontSize: "2rem", mb: 1 }}>📦</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], mb: "6px" }}>
              No files available for import
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
              Export a client from a development environment to see it here
            </Typography>
          </Box>
        ) : (
          files.map((file, idx) => {
            const checked = selectedFiles.includes(file.key);
            const createdAt = formatTimestamp(file.last_modified);
            return (
              <Box
                key={file.key}
                onClick={() => handleToggleFile(file.key)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "56px 1fr 160px",
                  alignItems: "center",
                  px: 3,
                  py: 1.5,
                  cursor: "pointer",
                  borderBottom: idx < files.length - 1 ? `1px solid ${SLATE[100]}` : "none",
                  bgcolor: checked ? INDIGO[50] : "transparent",
                  "&:hover": { bgcolor: checked ? INDIGO[50] : SLATE[50] },
                }}
              >
                <Checkbox
                  size="small"
                  checked={checked}
                  onChange={() => handleToggleFile(file.key)}
                  onClick={(e) => e.stopPropagation()}
                />
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: SLATE[900],
                    fontFamily: "monospace",
                  }}
                >
                  {displayName(file.key)}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500] }}>
                  {createdAt || "—"}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>

      <BottomActionBar
        name="Import Selected"
        handleSave={handleImport}
        cancelRedirectUrl="/clients"
        disabledSave={selectedFiles.length === 0}
        saving={importing}
      />
    </Box>
  );
}
